import { useRef, useState, useEffect } from "react";
import axios from "axios";
import { useDateFilterVersion } from "@/lib/useDateFilterVersion";
import { format } from "date-fns";
import { Plus, MagnifyingGlass, Eye, Warning, Trash, DownloadSimple, UploadSimple, Truck } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set()); // ids of picked invoices
  const [bulkBusy, setBulkBusy] = useState(false);
  const [dateMonth, setDateMonth] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [ewbDialogOpen, setEwbDialogOpen] = useState(false);
  const [ewbForm, setEwbForm] = useState({ distance_km: 0, vehicle_no: "", transporter_id: "", mode_of_transport: "1" });
  const [ewbBusy, setEwbBusy] = useState(false);
  const [ewbError, setEwbError] = useState("");
  const importRef = useRef(null);
  const navigate = useNavigate();

  const dfv = useDateFilterVersion();
  useEffect(() => { fetchInvoices(); }, [dfv]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let filtered = invoices;
    if (searchQuery) {
      filtered = filtered.filter((inv) =>
        inv.invoice_no.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.customer_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (statusFilter !== "all") {
      if (statusFilter === "overdue") {
        filtered = filtered.filter((inv) => isOverdue(inv));
      } else {
        filtered = filtered.filter((inv) => inv.status === statusFilter);
      }
    }
    if (dateMonth) {
      filtered = filtered.filter((inv) => (inv.invoice_date || "").startsWith(dateMonth));
    } else if (dateFrom || dateTo) {
      filtered = filtered.filter((inv) => {
        const d = (inv.invoice_date || "").slice(0, 10);
        return (!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo);
      });
    }
    setFilteredInvoices(filtered);
  }, [searchQuery, statusFilter, invoices, dateMonth, dateFrom, dateTo]);

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (importRef.current) importRef.current.value = "";
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/sales/import-excel`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(r.data);
      fetchInvoices();
    } catch (err) {
      setImportResult({ imported: 0, skipped_duplicates: 0, customers_created: 0, products_created: 0, errors: [err?.response?.data?.detail || "Import failed"] });
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/sales/import-template`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "Sales_Import_Template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { alert("Could not download template"); }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
      setFilteredInvoices(response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const isOverdue = (invoice) => {
    if (!invoice.due_date || invoice.status === "paid") return false;
    return new Date(invoice.due_date) < new Date();
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/invoices/${id}/status`, { status });
      fetchInvoices();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleDelete = async (id, invoiceNo) => {
    if (!window.confirm(`Delete invoice ${invoiceNo}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/invoices/${id}`);
      fetchInvoices();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to delete invoice");
    }
  };

  // ---- Bulk PDF download (ZIP) ----
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    const ids = filteredInvoices.map((i) => i.id);
    const allPicked = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPicked) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const bulkDownloadMerged = async () => {
    if (!selected.size) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/invoices/bulk-pdf-merged`, { ids: Array.from(selected) }, { responseType: "blob", timeout: 300000 });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url; a.download = `invoices-${ts}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      clearSelection();
    } catch (e) {
      let msg = e?.message || "Bulk PDF failed";
      const blob = e?.response?.data;
      if (blob && typeof blob.text === "function") {
        try { const t = await blob.text(); msg = JSON.parse(t).detail || t; } catch (_) { /* keep default msg */ }
      }
      alert(msg);
    } finally { setBulkBusy(false); }
  };

  const bulkDownload = async () => {
    if (!selected.size) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/invoices/bulk-pdf`, { ids: Array.from(selected) }, { responseType: "blob", timeout: 300000 });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url; a.download = `invoices-${ts}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      clearSelection();
    } catch (e) {
      let msg = e?.message || "Bulk download failed";
      const blob = e?.response?.data;
      if (blob && typeof blob.text === "function") {
        try { const t = await blob.text(); msg = JSON.parse(t).detail || t; } catch (_) { /* keep default msg */ }
      }
      alert(msg);
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkEwbJson = async () => {
    if (!selected.size) return;
    setEwbBusy(true);
    setEwbError("");
    try {
      const r = await axios.post(`${API}/ewb/bulk-json`, {
        invoice_ids: Array.from(selected),
        distance_km: parseInt(ewbForm.distance_km || 0, 10),
        vehicle_no: ewbForm.vehicle_no || "",
        transporter_id: ewbForm.transporter_id || "",
        mode_of_transport: ewbForm.mode_of_transport,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `eway_bulk_${ts}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setEwbDialogOpen(false);
      clearSelection();
    } catch (e) {
      let msg = "E-Way JSON download failed";
      const blob = e?.response?.data;
      if (blob && typeof blob.text === "function") {
        try { msg = JSON.parse(await blob.text()).detail || msg; } catch (_) { /* keep */ }
      }
      setEwbError(msg);
    } finally {
      setEwbBusy(false);
    }
  };

  return (
    <div className="p-4 sm:p-8" data-testid="invoices-page">
      <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-2">Sales</h1>
          <p className="text-slate-500 text-sm">Manage all your sales invoices</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={downloadTemplate} data-testid="download-template-btn" className="px-3 py-2 border border-slate-300 text-slate-700 rounded-sm text-sm font-semibold hover:bg-slate-50 transition-all duration-200 flex items-center gap-2">
            <DownloadSimple size={18} weight="bold" /> Download Format
          </button>
          <input ref={importRef} type="file" accept=".xlsx" onChange={handleImportExcel} className="hidden" data-testid="import-excel-input" />
          <button onClick={() => importRef.current?.click()} disabled={importing} data-testid="import-excel-btn" className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded-sm text-sm font-semibold hover:bg-emerald-50 transition-all duration-200 flex items-center gap-2 disabled:opacity-50">
            <UploadSimple size={18} weight="bold" /> {importing ? "Importing…" : "Import Excel"}
          </button>
          <button onClick={() => navigate("/invoices/create")} data-testid="create-invoice-btn" className="px-4 py-2 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 transition-all duration-200 flex items-center gap-2">
            <Plus size={20} weight="bold" /> Create Invoice
          </button>
        </div>
      </div>

      {importResult && (
        <div className={`mb-6 rounded-sm border px-4 py-3 text-sm ${importResult.imported > 0 ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="import-result-banner">
          <div className="flex items-center justify-between gap-3">
            <p className="font-semibold">
              Imported <b>{importResult.imported}</b> bill{importResult.imported === 1 ? "" : "s"}
              {importResult.skipped_duplicates > 0 && <> · <b>{importResult.skipped_duplicates}</b> skipped (duplicates/errors)</>}
              {importResult.customers_created > 0 && <> · <b>{importResult.customers_created}</b> new customers</>}
              {importResult.products_created > 0 && <> · <b>{importResult.products_created}</b> new products + stock updated</>}
            </p>
            <button onClick={() => setImportResult(null)} className="text-xs font-bold underline shrink-0">Dismiss</button>
          </div>
          {importResult.errors?.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs space-y-0.5">
              {importResult.errors.slice(0, 8).map((er, i) => <li key={i}>{er}</li>)}
              {importResult.errors.length > 8 && <li>…and {importResult.errors.length - 8} more</li>}
            </ul>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex gap-3 flex-wrap items-end">
        <div className="flex-1 min-w-[220px] relative">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" data-testid="search-invoices-input" placeholder="Search by invoice number or customer..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
        </div>
        <select data-testid="status-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-semibold">
          <option value="all">All Status</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
          <option value="partial">Partial</option>
          <option value="overdue">Overdue</option>
        </select>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500">Month</label>
          <input type="month" data-testid="sales-month-filter" value={dateMonth} onChange={(e) => { setDateMonth(e.target.value); if (e.target.value) { setDateFrom(""); setDateTo(""); } }} className="px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500">From</label>
          <input type="date" data-testid="sales-from-filter" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); if (e.target.value) setDateMonth(""); }} className="px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500">To</label>
          <input type="date" data-testid="sales-to-filter" value={dateTo} onChange={(e) => { setDateTo(e.target.value); if (e.target.value) setDateMonth(""); }} className="px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        {(dateMonth || dateFrom || dateTo) && (
          <button data-testid="sales-date-clear" onClick={() => { setDateMonth(""); setDateFrom(""); setDateTo(""); }} className="px-3 py-2.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-100">Clear</button>
        )}
      </div>

      {/* Bulk selection banner */}
      {selected.size > 0 && (
        <div className="mb-4 bg-slate-900 text-white rounded-sm px-4 py-3 flex items-center justify-between" data-testid="bulk-selection-banner">
          <div className="text-sm font-semibold">
            {selected.size} invoice{selected.size > 1 ? "s" : ""} selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setEwbError(""); setEwbDialogOpen(true); }}
              disabled={bulkBusy || ewbBusy}
              data-testid="bulk-ewb-json-btn"
              className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500 text-slate-900 rounded-sm text-xs font-bold uppercase tracking-widest disabled:opacity-60"
            >
              <Truck size={14} weight="bold" />
              E-Way JSON
            </button>
            <button
              onClick={bulkDownloadMerged}
              disabled={bulkBusy}
              data-testid="bulk-download-merged-pdf-btn"
              className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500 text-white rounded-sm text-xs font-bold uppercase tracking-widest disabled:opacity-60"
            >
              <DownloadSimple size={14} weight="bold" />
              {bulkBusy ? "Preparing…" : "Download PDF"}
            </button>
            <button
              onClick={bulkDownload}
              disabled={bulkBusy}
              data-testid="bulk-download-pdf-btn"
              className="inline-flex items-center gap-2 px-4 py-1.5 bg-white text-slate-900 rounded-sm text-xs font-bold uppercase tracking-widest disabled:opacity-60"
            >
              <DownloadSimple size={14} weight="bold" />
              {bulkBusy ? "Bundling…" : "Download PDFs (ZIP)"}
            </button>
            <button
              onClick={clearSelection}
              disabled={bulkBusy}
              data-testid="bulk-clear-btn"
              className="px-3 py-1.5 text-xs text-slate-300 hover:text-white underline"
            >Clear</button>
          </div>
        </div>
      )}

      {/* Invoices Table */}
      <div className="bg-white border border-slate-200 rounded-sm">
        {loading ? (
          <div className="p-12 animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (<div key={i} className="h-12 bg-slate-200 rounded"></div>))}
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="p-12 text-center">
            <img src="https://static.prod-images.emergentagent.com/jobs/c2875c4c-3209-4e71-8b15-de610df9cec1/images/6f94e3573c58a84c73fc78db7a1660d6d91c6b0e4489f8721400bcc17382d354.png" alt="No invoices" className="w-64 h-64 mx-auto mb-6 opacity-60" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">No invoices found</h3>
            <p className="text-slate-500 mb-6">Create your first invoice to get started</p>
            <button onClick={() => navigate("/invoices/create")} data-testid="empty-create-invoice-btn" className="px-6 py-3 bg-blue-600 text-white rounded-sm font-semibold hover:bg-blue-700 transition-all duration-200">Create Invoice</button>
          </div>
        ) : (
          <div className="overflow-x-auto sticky-table">
            <table className="w-full">
              <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-4 w-10 text-left">
                    <input
                      type="checkbox"
                      data-testid="select-all-invoices"
                      checked={filteredInvoices.length > 0 && filteredInvoices.every((i) => selected.has(i.id))}
                      onChange={toggleAllVisible}
                      className="w-4 h-4"
                      aria-label="Select all visible invoices"
                    />
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Invoice No</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Customer</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Date</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Due Date</th>
                  <th className="px-3 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Taxable</th>
                  <th className="px-3 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">CGST</th>
                  <th className="px-3 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">SGST</th>
                  <th className="px-3 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">IGST</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Total</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Paid</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Status</th>
                  <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredInvoices.map((invoice) => {
                  const overdue = isOverdue(invoice);
                  return (
                    <tr key={invoice.id} data-testid={`invoice-row-${invoice.invoice_no}`} className={`hover:bg-slate-50 transition-colors duration-150 ${overdue ? "bg-red-50/50" : ""} ${selected.has(invoice.id) ? "bg-slate-100/70" : ""}`}>
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          data-testid={`select-invoice-${invoice.invoice_no}`}
                          checked={selected.has(invoice.id)}
                          onChange={() => toggleOne(invoice.id)}
                          className="w-4 h-4"
                          aria-label={`Select invoice ${invoice.invoice_no}`}
                        />
                      </td>
                      <td className="px-5 py-4 text-sm font-semibold text-slate-900">{invoice.invoice_no}</td>
                      <td className="px-5 py-4">
                        <p className="text-sm font-semibold text-slate-900">{invoice.customer_name}</p>
                        <p className="text-xs text-slate-500">{invoice.customer_state}</p>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">{format(new Date(invoice.invoice_date), "dd MMM yyyy")}</td>
                      <td className="px-5 py-4 text-sm">
                        {invoice.due_date ? (
                          <span className={overdue ? "text-red-600 font-semibold" : "text-slate-600"}>
                            {format(new Date(invoice.due_date), "dd MMM yyyy")}
                            {overdue && <Warning size={14} weight="fill" className="inline ml-1 text-red-500" />}
                          </span>
                        ) : (
                          <span className="text-slate-400">--</span>
                        )}
                      </td>
                      <td className="px-3 py-4 text-xs text-right text-slate-700">{"\u20b9"}{(invoice.subtotal || 0).toFixed(2)}</td>
                      <td className="px-3 py-4 text-xs text-right text-blue-600">{invoice.cgst ? `\u20b9${invoice.cgst.toFixed(2)}` : "--"}</td>
                      <td className="px-3 py-4 text-xs text-right text-blue-600">{invoice.sgst ? `\u20b9${invoice.sgst.toFixed(2)}` : "--"}</td>
                      <td className="px-3 py-4 text-xs text-right text-purple-600">{invoice.igst ? `\u20b9${invoice.igst.toFixed(2)}` : "--"}</td>
                      <td className="px-5 py-4 text-sm font-semibold text-slate-900">{"\u20b9"}{invoice.total.toFixed(2)}</td>
                      <td className="px-5 py-4 text-sm text-green-700 font-semibold">{"\u20b9"}{(invoice.amount_paid || 0).toFixed(2)}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wider ${
                          invoice.status === "paid" ? "bg-green-100 text-green-800"
                            : invoice.status === "partial" ? "bg-blue-100 text-blue-800"
                            : overdue ? "bg-red-100 text-red-800"
                            : "bg-amber-100 text-amber-800"
                        }`}>
                          {overdue && invoice.status !== "paid" ? "OVERDUE" : invoice.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => navigate(`/invoices/${invoice.id}`)} data-testid={`view-invoice-${invoice.invoice_no}`} className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-sm transition-all duration-200">
                            <Eye size={18} />
                          </button>
                          <button onClick={() => handleDelete(invoice.id, invoice.invoice_no)} data-testid={`delete-invoice-${invoice.invoice_no}`} className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-sm transition-all duration-200">
                            <Trash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bulk E-Way JSON Dialog */}
      <Dialog open={ewbDialogOpen} onOpenChange={setEwbDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk E-Way Bill JSON ({selected.size} invoices)</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">
            Official NIC bulk format — download and upload it at <a href="https://ewaybillgst.gov.in" target="_blank" rel="noreferrer" className="text-blue-600 underline">ewaybillgst.gov.in</a> under <b>e-Waybill → Generate Bulk</b>. All EWBs are generated together (100% free).
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Approx Distance (km)</Label>
              <Input type="number" min="0" data-testid="bulk-ewb-distance" value={ewbForm.distance_km} onChange={(e) => setEwbForm({ ...ewbForm, distance_km: e.target.value })} />
            </div>
            <div>
              <Label>Vehicle No (common)</Label>
              <Input data-testid="bulk-ewb-vehicle" value={ewbForm.vehicle_no} onChange={(e) => setEwbForm({ ...ewbForm, vehicle_no: e.target.value.toUpperCase() })} placeholder="MH01AB1234" />
            </div>
            <div>
              <Label>Transporter ID</Label>
              <Input data-testid="bulk-ewb-transporter" value={ewbForm.transporter_id} onChange={(e) => setEwbForm({ ...ewbForm, transporter_id: e.target.value })} placeholder="Optional" />
            </div>
            <div>
              <Label>Mode</Label>
              <select data-testid="bulk-ewb-mode" value={ewbForm.mode_of_transport} onChange={(e) => setEwbForm({ ...ewbForm, mode_of_transport: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
                <option value="1">Road</option>
                <option value="2">Rail</option>
                <option value="3">Air</option>
                <option value="4">Ship</option>
              </select>
            </div>
          </div>
          {ewbError && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded-sm" data-testid="bulk-ewb-error">{ewbError}</div>}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEwbDialogOpen(false)} disabled={ewbBusy}>Cancel</Button>
            <Button onClick={bulkEwbJson} disabled={ewbBusy} data-testid="bulk-ewb-download-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
              {ewbBusy ? "Building…" : "Download JSON"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
